import { useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import cc from 'classcat';
import {
  competitions,
  PREMATCH_GAME_TYPE_FOR_SWARM,
  regions,
  sports
} from 'utils/constants/sportsbook/data-fields';
import { mountPoint } from 'utils/generic/route-manipulation';
import RidGenerator from 'utils/swarm/rid-generator';
import { Sport } from 'interfaces/sportsbook-data-levels';
import { executeCommand, unsubscribeCommand } from 'services/get-data-level';
import {
  CompetitionsFilters,
  EAdvancedCompetitionsFilters
} from 'newelements/Sportsbook/layouts/advanced/__partials/AdvancedMainContent/__partials/AdvancedFilters/CompetitionsFilters';
import { FilteredByTimeWrapper } from 'newelements/Sportsbook/layouts/advanced/__partials/AdvancedMainContent/__partials/AdvancedGamesByCompetitions/FilteredByTimeWrapper';
import {
  AdvancedHeaderBreadcrumb,
  TBreadcrumbItemType
} from 'newelements/Sportsbook/layouts/advanced/__partials/AdvancedMainContent/__partials/AdvancedHeaderBreadcrumb';
import {
  TFilteredCouponLayoutsForRanges,
  TIME_RANGES_BY_COUPON_LAYOUT
} from 'newelements/Sportsbook/layouts/advanced/constants/coupon-time-ranges';
import { NoItem } from 'components/Shared/NoItem';
import { GamesFilteredByLeagues } from 'newcomponents/Shared/sportsbook/GameFilteredByLeagues/GamesFilteredByLeagues';
import { Skeleton } from 'newcomponents/UI/Skeleton';
import { useMounted } from 'hooks/useMounted';
import { useSearchParams } from 'hooks/useSearchParams';
import {
  getAdvancedSelectedRangeFilter,
  getSportsbookSelectedSport
} from 'store/selectors/new-sportsbook';
import './index.less';

type TRequestWhere = {
  competition: { id: { '@in': number[] } };
  game: {
    type: { '@in': number[] };
    start_ts?: { '@gt': number; '@lt': number };
  };
};

export const AdvancedGamesByCompetitions = () => {
  const history = useHistory();
  const { mounted } = useMounted();
  const { t } = useTranslation();
  const {
    compIds,
    filterBy,
    coupon: selectedCouponInQuery
  }: {
    compIds?: string;
    filterBy?: EAdvancedCompetitionsFilters;
    coupon?: TFilteredCouponLayoutsForRanges;
  } = useSearchParams();

  const mainSportsMenuSelectedSport = useSelector(getSportsbookSelectedSport);
  const selectedTimeRange = useSelector(getAdvancedSelectedRangeFilter);

  const [sportsData, setSportsData] = useState<Record<string, Sport> | null>(
    null
  );

  const breadcrumbItems: TBreadcrumbItemType[] = useMemo(() => {
    if (compIds && filterBy) {
      return [
        {
          id: 1,
          title: t('sportsbook.competitions'),
          active: true
        }
      ];
    }

    if (selectedCouponInQuery) {
      return [
        {
          id: 1,
          title: t(`sportsbook.${selectedCouponInQuery}`),
          active: true
        },
        {
          id: 2,
          title: `${mainSportsMenuSelectedSport?.name}` || (
            <Skeleton.Button
              active
              style={{
                width: 180,
                height: 20
              }}
            />
          ),
          onClick: () => {
            if (mainSportsMenuSelectedSport?.alias) {
              const redirectPath = `${mountPoint(window.getPathname())}/${
                mainSportsMenuSelectedSport?.alias
              }`;

              history.push(redirectPath);
            }
          }
        }
      ];
    }

    return [];
  }, [
    !!compIds,
    !!filterBy,
    !!selectedCouponInQuery,
    mainSportsMenuSelectedSport?.name
  ]);

  const getSportsData = (rId: string, where: Record<string, any>) => {
    const what = {
      sport: sports.sport,
      region: regions.region,
      competition: competitions.competition,
      game: ['id', 'type', 'start_ts']
    };

    const onResponse = (data: { sport: Record<string, Sport> }) => {
      if (data && data.sport && mounted.current) {
        setSportsData(data.sport);
      }
    };

    executeCommand(rId, what, where, onResponse, true, onResponse);
  };

  useLayoutEffect(() => {
    if (compIds && !selectedCouponInQuery) {
      const idsArray = compIds.split(',').map(Number);

      const rId = RidGenerator.gForSubscribe(
        `ADVANCED_GET_GAMES_BY_COMPETITIONS`
      );

      const where: TRequestWhere = {
        competition: { id: { '@in': idsArray } },
        game: { type: { '@in': [0, 1, 2] } }
      };

      if (selectedTimeRange?.ranges) {
        where.game.start_ts = selectedTimeRange.ranges;
      }

      getSportsData(rId, where);

      return () => {
        unsubscribeCommand(rId);
      };
    }
  }, [compIds, selectedTimeRange]);

  useLayoutEffect(() => {
    if (!compIds && selectedCouponInQuery && mainSportsMenuSelectedSport) {
      const rId = RidGenerator.gForSubscribe(
        `ADVANCED_GET_COUPON_GAMES_BY_SPORT`
      );

      const ranges = TIME_RANGES_BY_COUPON_LAYOUT[selectedCouponInQuery];

      const where = {
        sport: { alias: mainSportsMenuSelectedSport.alias },
        game: {
          start_ts: ranges,
          ...PREMATCH_GAME_TYPE_FOR_SWARM
        }
      };

      getSportsData(rId, where);

      return () => {
        unsubscribeCommand(rId);
      };
    }
  }, [mainSportsMenuSelectedSport?.alias]);

  const noGamesAvailable = sportsData && Object.keys(sportsData).length === 0;

  return (
    <>
      <AdvancedHeaderBreadcrumb additionalNavigationItems={breadcrumbItems} />
      <div
        className={cc([
          'advancedGamesByCompetitionsWrapper',
          {
            'advancedGamesByCompetitionsWrapper--noGames': noGamesAvailable
          }
        ])}
      >
        <CompetitionsFilters />
        {noGamesAvailable ? (
          <NoItem
            className="advancedNoGamesByCompetitions"
            size="medium"
            title={t('sportsbook.noGame')}
            text={t('sportsbook.noGameText')}
          />
        ) : filterBy === EAdvancedCompetitionsFilters.LEAGUE ? (
          <GamesFilteredByLeagues sportsData={sportsData} />
        ) : (
          <FilteredByTimeWrapper sportsData={sportsData} />
        )}
      </div>
    </>
  );
};
